import React, { useMemo } from 'react';

import styled from 'styled-components';

import { mobile } from '../../styles';

import { useCartDrawerFreeShippingProgressBar } from './useCartDrawerProgressBar';

const ProgressBarWrapper = styled.div.attrs({
  'data-testid': 'NewCartDrawerProgressBar',
})`
  ${mobile`
    width: calc(100% - ${props => props.theme.spacing.small * 2}px);
    margin: 0 ${({ theme }) => theme.spacing.small}px;
    margin-bottom: ${({ theme }) => theme.spacing.moderate}px;
  `}
  width: calc(100% - ${({ theme }) => theme.spacing.small * 2}px);
  margin: auto;
  background-color: ${({ theme }) => theme.colors.marble};
  border-radius: 32px;
  height: 32px;
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing.moderate}px;
`;

const ProgressBarInner = styled.div<{ progressPercentage: number }>`
  height: inherit;
  width: ${props => props.progressPercentage}%;
  background-color: ${({ theme }) => theme.colors.lavender200};
  border-radius: inherit;
  transition: width 0.4s cubic-bezier(0.42, 0, 0.58, 1);
`;

const ProgressBarMessage = styled.div<{ goalIsReached: boolean }>`
  position: absolute;
  width: 100%;
  top: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.gunmetal};
  text-transform: uppercase;
  ${({ goalIsReached, theme }) =>
    goalIsReached
      ? theme.subHeader.variants.subHeading5Uppercase.textStyles
      : theme.paragraph.variants.paragraph5Uppercase.textStyles};

  & p,
  & span {
    display: inline;
  }
`;

type ProgressParams = {
  currentValue: number;
  goalValue: number;
};

export function calculateProgress({ currentValue, goalValue }: ProgressParams) {
  if (currentValue < 0 || goalValue <= 0) {
    return 0;
  }
  return currentValue >= goalValue
    ? 100
    : 100 - Math.ceil(((goalValue - currentValue) / goalValue) * 100);
}

export function calculateRemainingToGoal({
  currentValue,
  goalValue,
}: ProgressParams) {
  return currentValue >= goalValue
    ? 0
    : Number((goalValue - currentValue).toFixed(2));
}

export function useIsFreeShippingAvailableAndThresholdIsReached() {
  const { total, shouldShowProgressBar, threshold } =
    useCartDrawerFreeShippingProgressBar();
  return useMemo(
    () =>
      shouldShowProgressBar &&
      calculateRemainingToGoal({
        currentValue: total,
        goalValue: threshold,
      }) === 0,
    [total, shouldShowProgressBar, threshold]
  );
}

type RenderPropChildren = ({
  amountToGoal,
}: {
  amountToGoal: number;
}) => JSX.Element;

type Props = {
  render: RenderPropChildren | JSX.Element;
  progress?: ProgressParams;
};

export function NewCartDrawerProgressBar({
  progress,
  render,
}: Props): JSX.Element {
  if (typeof render === 'function') {
    const percentage = calculateProgress(progress);
    const amountToGoal = calculateRemainingToGoal(progress);

    return (
      <ProgressBarWrapper>
        <ProgressBarInner progressPercentage={percentage} />
        <ProgressBarMessage goalIsReached={percentage === 100}>
          {render({ amountToGoal })}
        </ProgressBarMessage>
      </ProgressBarWrapper>
    );
  }
  return (
    <ProgressBarWrapper>
      <ProgressBarInner progressPercentage={100} />
      <ProgressBarMessage goalIsReached>{render}</ProgressBarMessage>
    </ProgressBarWrapper>
  );
}
